import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createRegion,
  getAllRegions,
  getRegionByID,
  updateRegion,
} from "./services/region.services";
import { clearRegionDetails } from "./regionSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import { closed } from "../../components/muidrawer/muiDrawerSlice";

const CreateRegion: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const RegionDetail = useAppSelector((state) => state.region.RegionDetail);
  const RegionID = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector((state) => state.region.formLoading);
  const [formKey, setFormKey] = React.useState(0);

  useEffect(() => {
    if (RegionID) {
      dispatch(getRegionByID(RegionID));
    }
  }, [RegionID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [RegionDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearRegionDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!RegionID) {
        const insertPayload = {
          RegionName: values.RegionName,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createRegion(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRegionDetails());
          dispatch(getAllRegions());
        }
      } else {
        const editPayload = {
          ID: RegionID,
          RegionName: values.RegionName,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateRegion(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRegionDetails());
          dispatch(getAllRegions());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={RegionDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {RegionID ? "Update Region" : "Add Region"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </IconButton>
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="RegionName"
                  label="Region"
                  placeholder="Region"
                  astrike={true}
                  component={FormTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                style={{
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={RegionID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateRegion;
